/**
 * BillingPanel - Premium dark+gold plan details modal
 * Shows current plan status, download usage, and contextual upgrade CTA
 */
import { X, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useEffect } from "react";

export function BillingPanel({ user, onClose, onUpgrade }) {
  const plan = user?.agency_plan ? "agency" : user?.plan === "paid" ? "paid" : "free";
  const freeLimit = user?.free_download_limit ?? 2;
  const used = user?.downloads_used ?? 0;
  const remaining = plan === "free" ? Math.max(0, freeLimit - used) : null;
  const freeExhausted = plan === "free" && remaining === 0;

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="billing-panel">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative z-10 max-w-[400px] w-full mx-4 rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(165deg, #1c1917 0%, #292524 40%, #1c1917 100%)",
          boxShadow: "0 0 80px rgba(202,138,4,0.12), 0 25px 60px rgba(0,0,0,0.5)",
        }}
        data-testid="billing-panel-content"
      >
        {/* Gold accent bar */}
        <div className="h-1" style={{ background: "linear-gradient(90deg, #92400e, #d97706, #f59e0b, #d97706, #92400e)" }} />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
          style={{ background: "rgba(255,255,255,0.08)" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
          data-testid="billing-close-btn"
        >
          <X className="h-4 w-4" style={{ color: "#8a7e6e" }} />
        </button>

        <div className="px-6 sm:px-8 pt-7 pb-6">
          {/* Header */}
          <p className="text-[10px] font-medium tracking-[0.25em] uppercase mb-2" style={{ color: "#a8956e" }}>
            Billing & Plans
          </p>
          <h2
            className="text-lg font-bold mb-5 tracking-tight"
            style={{ color: "#f5f0e8", fontFamily: "Inter, system-ui, sans-serif" }}
            data-testid="billing-title"
          >
            Your Plan
          </h2>

          {/* Plan card */}
          <div
            className="rounded-xl px-5 py-4 mb-5"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(184,151,58,0.25)",
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold" style={{ color: "#f5f0e8" }} data-testid="billing-plan-name">
                {plan === "free" ? "Free Trial Plan" : plan === "agency" ? "Agency Plan" : "Studio Pass 2.0"}
              </span>
              <span
                className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                style={{
                  background: freeExhausted ? "rgba(239,68,68,0.15)" : "rgba(16,185,129,0.15)",
                  color: freeExhausted ? "#f87171" : "#34d399",
                }}
                data-testid="billing-plan-status"
              >
                {freeExhausted ? "Exhausted" : "Active"}
              </span>
            </div>

            <div className="h-px mb-3" style={{ background: "rgba(184,151,58,0.15)" }} />

            {/* Plan-specific details */}
            {plan === "free" && (
              <div className="space-y-2">
                <DetailRow label="Plan type" value="Free Trial" />
                <DetailRow label="Total free samples" value={`${freeLimit}`} />
                <DetailRow label="Downloads used" value={`${used}`} />
                <DetailRow
                  label="Remaining"
                  value={remaining > 0 ? `${remaining} download${remaining !== 1 ? "s" : ""}` : "None"}
                  valueColor={remaining > 0 ? "#34d399" : "#f87171"}
                />
              </div>
            )}

            {plan === "paid" && (
              <div className="space-y-2">
                <DetailRow label="Plan type" value="Studio Pass 2.0" />
                <DetailRow label="Access" value="Full Library" />
                <DetailRow label="Total downloads" value={`${used}`} />
                <DetailRow label="Monthly limit" value="500 / month (fair use)" />
              </div>
            )}

            {plan === "agency" && (
              <div className="space-y-2">
                <DetailRow label="Plan type" value="Agency Plan" />
                <DetailRow label="Access" value="Full Library + Extended" />
                <DetailRow label="Total downloads" value={`${used}`} />
                <DetailRow label="Monthly limit" value="Unlimited" />
              </div>
            )}
          </div>

          {/* Free exhausted — upgrade CTA */}
          {freeExhausted && (
            <>
              <p className="text-center text-[13px] mb-4" style={{ color: "#b0a594" }}>
                Your free samples are exhausted.<br />
                Upgrade to get unlimited access.
              </p>
              <button
                data-testid="billing-upgrade-btn"
                onClick={() => { onClose(); onUpgrade(); }}
                className="w-full h-12 flex items-center justify-center gap-2 rounded-xl text-sm font-bold tracking-wide transition-all duration-200"
                style={{
                  background: "linear-gradient(135deg, #92400e 0%, #d97706 30%, #f59e0b 50%, #d97706 70%, #92400e 100%)",
                  color: "#1c1917",
                  boxShadow: "0 0 24px rgba(245,158,11,0.25), 0 4px 12px rgba(0,0,0,0.3)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 36px rgba(245,158,11,0.4), 0 6px 16px rgba(0,0,0,0.3)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 24px rgba(245,158,11,0.25), 0 4px 12px rgba(0,0,0,0.3)"; }}
              >
                <Sparkles className="h-4 w-4" />
                Upgrade to Studio Pass 2.0
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}

          {/* Free with remaining — subtle upgrade hint */}
          {plan === "free" && !freeExhausted && (
            <button
              data-testid="billing-upgrade-btn"
              onClick={() => { onClose(); onUpgrade(); }}
              className="w-full py-2.5 rounded-xl text-sm font-medium transition-colors text-center"
              style={{ color: "#a8956e", border: "1px solid rgba(184,151,58,0.2)" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#d97706"; e.currentTarget.style.borderColor = "rgba(184,151,58,0.4)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#a8956e"; e.currentTarget.style.borderColor = "rgba(184,151,58,0.2)"; }}
            >
              Upgrade to Studio Pass 2.0
            </button>
          )}

          {/* Paid — confirmation message */}
          {plan === "paid" && (
            <div className="flex items-center justify-center gap-1.5 mt-1">
              <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "#34d399" }} />
              <span className="text-[12px]" style={{ color: "#8a7e6e" }}>Full library access active</span>
            </div>
          )}

          {/* Agency — top tier message */}
          {plan === "agency" && (
            <div className="flex items-center justify-center gap-1.5 mt-1">
              <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "#a78bfa" }} />
              <span className="text-[12px]" style={{ color: "#8a7e6e" }}>You are on the highest plan</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, valueColor }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[12px]" style={{ color: "#8a7e6e" }}>{label}</span>
      <span className="text-[12px] font-medium" style={{ color: valueColor || "#d6cfc3" }}>{value}</span>
    </div>
  );
}
